import { useState, useEffect, useCallback, memo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Card,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Autocomplete,
  Checkbox,
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { orderService } from '../services'
import { formatOrganizationLabel } from '../utils/organizationLabel'
import { useListQueryState } from '../hooks/useListQueryState'
import { useOrganizationMultiSearch } from '../hooks/useOrganizationMultiSearch'
import {
  buildOrderDetailPath,
  buildOrdersListSearchParams,
  formatStatusLabel,
  PARTIALLY_COMPLETED_LABEL,
  getOrdersListReturnPath,
  ordersListHasExplicitParams,
  parseOrdersListSearchParams,
  toOrdersApiParams,
} from '../utils/ordersListUtils'
import {
  PAYMENT_STATUS_FILTER_LABELS,
  PURCHASE_ORDER_PAYMENT_STATUS_FILTERS,
} from '../utils/paymentStatusDisplay'
import useAuthStore from '../store/authStore'
import {
  canExportData,
  hasAdminPermission,
  PERMISSION_SUBJECTS,
} from '../utils/adminPermissions'
import AddIcon from '@mui/icons-material/Add'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import HistoryIcon from '@mui/icons-material/History'
import OrdersSearchBar from '../components/orders/OrdersSearchBar'
import OrdersListTable from '../components/orders/OrdersListTable'
import PurchaseOrderExportDialog from '../components/orders/PurchaseOrderExportDialog'

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

const ActionLabel = ({ shortLabel, fullLabel }) => (
  <>
    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
      {shortLabel}
    </Box>
    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
      {fullLabel}
    </Box>
  </>
)

const ShopFilterCard = memo(function ShopFilterCard({
  selectedOrgIds,
  onSelectedOrgIdsChange,
  orderNoun = 'orders',
}) {
  const {
    searchText,
    setSearchText,
    options: organizations,
    loading: orgsLoading,
    selectedOrgs,
    setSelectedFromOrgs,
    selectAllVisible,
    clearSelected,
  } = useOrganizationMultiSearch({
    enabled: true,
    selectedIds: selectedOrgIds,
  })

  return (
    <Card sx={{ mb: 3, p: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
        Shop
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          loading={orgsLoading}
          options={organizations}
          filterOptions={(opts) => opts}
          inputValue={searchText}
          onInputChange={(_, value, reason) => {
            if (reason === 'input' || reason === 'clear') {
              setSearchText(value || '')
            }
          }}
          getOptionLabel={(o) => formatOrganizationLabel(o)}
          isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
          value={selectedOrgs}
          onChange={(_, newValue) => {
            onSelectedOrgIdsChange(setSelectedFromOrgs(newValue))
          }}
          filterSelectedOptions={false}
          sx={{ flex: '1 1 320px', minWidth: 260, maxWidth: 560 }}
          renderOption={(props, option, { selected }) => {
            const { key, ...rest } = props
            return (
              <li key={key} {...rest}>
                <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                {formatOrganizationLabel(option)}
              </li>
            )
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Shop"
              placeholder="Search shop name or Shop Id…"
              size="small"
              helperText="Type to search all shops by name or Shop Id."
            />
          )}
        />
        <Button
          variant="outlined"
          size="small"
          disabled={!organizations.length}
          onClick={() => onSelectedOrgIdsChange(selectAllVisible())}
          sx={{ color: '#e90000', borderColor: '#e90000', height: 40 }}
        >
          Select all
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onSelectedOrgIdsChange(clearSelected())}
          sx={{ height: 40 }}
        >
          Clear
        </Button>
      </Box>
      <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 1 }}>
        Leave none selected to show {orderNoun} for every shop. When you pick one or more, only those
        shops are sent to the API. Select all applies to the current search results.
      </Typography>
    </Card>
  )
})

const OrdersListPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isSales = location.pathname.startsWith('/orders/sales')
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canExportOrders = canExportData(adminPermissions)
  const canCreateOrder = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.ORDER,
    'create',
  )
  const routeKey = isSales ? 'orders.sales' : 'orders.purchase'

  const parseOrdersFilters = useCallback(
    (sp) =>
      parseOrdersListSearchParams(sp, {
        includeStatus: !isSales,
      }),
    [isSales],
  )

  const buildOrdersFilters = useCallback(
    (f) =>
      buildOrdersListSearchParams({
        q: f.q,
        status: isSales ? '' : f.status,
        paymentStatus: isSales ? '' : f.paymentStatus,
        page: f.page,
        limit: f.limit,
        orgIds: f.orgIds || [],
      }),
    [isSales],
  )

  const ordersLabelFromFilters = useCallback(
    (f) => {
      const parts = []
      if ((f.q || '').trim()) parts.push(`“${f.q.trim()}”`)
      if (!isSales && f.status) parts.push(f.status)
      if (!isSales && f.paymentStatus) parts.push(f.paymentStatus)
      if (f.orgIds?.length) parts.push(`${f.orgIds.length} shops`)
      return parts.length ? parts.join(' · ') : 'Order filters'
    },
    [isSales],
  )

  const ordersIsDefaultFilters = useCallback(
    (f) =>
      !(f.q || '').trim() &&
      !f.status &&
      !f.paymentStatus &&
      !(f.orgIds || []).length &&
      (f.page || 0) === 0 &&
      (f.limit || 10) === 10,
    [],
  )

  const {
    filters,
    setFilters,
    resetFilters,
    applyRecent,
    recent,
    isDefault,
  } = useListQueryState({
    routeKey,
    parse: parseOrdersFilters,
    build: buildOrdersFilters,
    hasExplicitParams: ordersListHasExplicitParams,
    labelFromFilters: ordersLabelFromFilters,
    isDefaultFilters: ordersIsDefaultFilters,
  })

  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [totalCount, setTotalCount] = useState(0)

  const page = filters.page
  const rowsPerPage = filters.limit
  const statusFilter = isSales ? '' : filters.status || ''
  const paymentStatusFilter = isSales ? '' : filters.paymentStatus || ''
  const debouncedSearch = filters.q || ''
  const selectedOrgIds = filters.orgIds || []

  const [exportDialogOpen, setExportDialogOpen] = useState(false)

  const applySearchQuery = useCallback(
    (q) => {
      setFilters({ q, page: 0 }, { commitRecent: true })
    },
    [setFilters],
  )

  useEffect(() => {
    if (isSales) setExportDialogOpen(false)
  }, [isSales])

  useEffect(() => {
    const msg = location.state?.permissionDenied
    if (!msg || isSales) return
    setError(msg)
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: {} },
    )
  }, [location.state, location.pathname, location.search, isSales, navigate])

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const params = toOrdersApiParams(filters, { isSales })
      const response = await orderService.getAllOrders(params)
      setOrders(response.data || response.items || [])
      setTotalCount(response.pagination?.total || 0)
    } catch (err) {
      console.error('Error fetching orders:', err)
      setError(err.response?.data?.message || 'Failed to fetch orders')
    } finally {
      setLoading(false)
    }
  }, [filters, isSales])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  const handleChangePage = useCallback(
    (_event, newPage) => {
      setFilters({ page: newPage })
    },
    [setFilters],
  )

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      const nextLimit = parseInt(event.target.value, 10)
      setFilters({ page: 0, limit: nextLimit })
    },
    [setFilters],
  )

  const handleStatusFilterChange = useCallback(
    (event) => {
      setFilters({ status: event.target.value, page: 0 }, { commitRecent: true })
    },
    [setFilters],
  )

  const handlePaymentStatusFilterChange = useCallback(
    (event) => {
      setFilters(
        { paymentStatus: event.target.value, page: 0 },
        { commitRecent: true },
      )
    },
    [setFilters],
  )

  const handleRowClick = useCallback(
    (orderId) => {
      const returnParams = buildOrdersFilters(filters)
      const ordersListPath = getOrdersListReturnPath(
        location.pathname,
        returnParams,
      )
      navigate(buildOrderDetailPath(orderId, ordersListPath), {
        state: { ordersListPath },
      })
    },
    [buildOrdersFilters, filters, location.pathname, navigate],
  )

  const handleSelectedOrgIdsChange = useCallback(
    (ids) => {
      setFilters(
        { orgIds: ids.map((id) => String(id)), page: 0 },
        { commitRecent: true },
      )
    },
    [setFilters],
  )

  const handleExportQueued = useCallback(
    (response) => {
      setExportDialogOpen(false)
      navigate('/orders/purchase/exports', {
        state: {
          queuedMessage: response?.jobId
            ? `Export started (Job ID ${response.jobId}). Watching for progress…`
            : response?.message || 'Export started. Watching for progress…',
        },
      })
    },
    [navigate],
  )

  return (
    <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#1a1a1a',
              mb: 0.5,
              fontSize: { xs: '1.5rem', sm: '2.125rem' },
            }}
          >
            {isSales ? 'Sales orders' : 'Purchase orders'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            {isSales
              ? 'Browse sale orders across shops. Search by TPN, DN, order #, sales order #, shop ID, or shop name (partial match). Use the Shop filter separately when needed.'
              : 'View and manage purchase orders. Search by TPN, DN, order #, sales order #, shop ID, or shop name (partial match). Use the Shop filter separately when needed.'}
          </Typography>
        </Box>
      </Box>

      <ShopFilterCard
        selectedOrgIds={selectedOrgIds}
        onSelectedOrgIdsChange={handleSelectedOrgIdsChange}
        orderNoun={isSales ? 'sale orders' : 'purchase orders'}
      />

      <Box
        sx={{
          mb: 3,
          display: 'flex',
          gap: { xs: 1.5, sm: 2 },
          alignItems: { xs: 'stretch', md: 'flex-start' },
          flexWrap: 'wrap',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1.5, sm: 2 },
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            flex: '1 1 240px',
            minWidth: 0,
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <OrdersSearchBar
            appliedSearch={debouncedSearch}
            onApplySearchQuery={applySearchQuery}
            onClearFilters={resetFilters}
            isDefault={isDefault}
            recent={recent}
            onApplyRecent={applyRecent}
          />

          {!isSales && (
            <FormControl
              size="small"
              sx={{
                minWidth: 0,
                width: { xs: '100%', sm: 280 },
                flex: { xs: '1 1 100%', sm: '0 1 280px' },
              }}
            >
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Filter by Status"
                MenuProps={{
                  marginThreshold: 8,
                  PaperProps: {
                    sx: { maxWidth: 'calc(100vw - 24px)' },
                  },
                }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="CREATED">{formatStatusLabel('CREATED')}</MenuItem>
                <MenuItem value="MODIFIED">{formatStatusLabel('MODIFIED')}</MenuItem>
                <MenuItem value="ACCEPTED">{formatStatusLabel('ACCEPTED')}</MenuItem>
                <MenuItem value="REJECTED">{formatStatusLabel('REJECTED')}</MenuItem>
                <MenuItem value="PROCUREMENT_STARTED">{formatStatusLabel('PROCUREMENT_STARTED')}</MenuItem>
                <MenuItem value="READY_FOR_DISPATCH">{formatStatusLabel('READY_FOR_DISPATCH')}</MenuItem>
                <MenuItem value="DISPATCHED">{formatStatusLabel('DISPATCHED')}</MenuItem>
                <MenuItem value="COMPLETED">{formatStatusLabel('COMPLETED')}</MenuItem>
                <MenuItem value="PARTIALLY_COMPLETED">
                  {PARTIALLY_COMPLETED_LABEL}
                </MenuItem>
                <MenuItem value="CLOSED_WITH_PARTIAL_DELIVERY">
                  {formatStatusLabel('CLOSED_WITH_PARTIAL_DELIVERY')}
                </MenuItem>
                <MenuItem value="RETURN_IN_PROGRESS">{formatStatusLabel('RETURN_IN_PROGRESS')}</MenuItem>
                <MenuItem value="RETURN_REJECTED">{formatStatusLabel('RETURN_REJECTED')}</MenuItem>
                <MenuItem value="RETURN_ACCEPTED">{formatStatusLabel('RETURN_ACCEPTED')}</MenuItem>
                <MenuItem value="RETURN_ACCEPTED_WITH_RECONCILIATIONS">
                  {formatStatusLabel('RETURN_ACCEPTED_WITH_RECONCILIATIONS')}
                </MenuItem>
                <MenuItem value="DELIVERED">{formatStatusLabel('DELIVERED')}</MenuItem>
                <MenuItem value="CANCELLED">{formatStatusLabel('CANCELLED')}</MenuItem>
                <MenuItem value="FAILED">{formatStatusLabel('FAILED')}</MenuItem>
              </Select>
            </FormControl>
          )}

          {!isSales && (
            <FormControl
              size="small"
              sx={{
                minWidth: 0,
                width: { xs: '100%', sm: 280 },
                flex: { xs: '1 1 100%', sm: '0 1 280px' },
              }}
            >
              <InputLabel>Filter by Payment Status</InputLabel>
              <Select
                value={paymentStatusFilter}
                onChange={handlePaymentStatusFilterChange}
                label="Filter by Payment Status"
                MenuProps={{
                  marginThreshold: 8,
                  PaperProps: {
                    sx: { maxWidth: 'calc(100vw - 24px)' },
                  },
                }}
              >
                <MenuItem value="">All Payment Statuses</MenuItem>
                {PURCHASE_ORDER_PAYMENT_STATUS_FILTERS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {PAYMENT_STATUS_FILTER_LABELS[value]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {!isSales && (canExportOrders || canCreateOrder) && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              width: { xs: '100%', md: 'auto' },
              flex: { xs: '1 1 100%', md: '0 1 auto' },
              ml: { md: 'auto' },
              minWidth: 0,
            }}
          >
            {canExportOrders && (
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => navigate('/orders/purchase/exports')}
                aria-label="View purchase order exports"
                sx={{
                  height: 40,
                  flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
                  minWidth: { xs: 0, sm: 'auto' },
                  whiteSpace: 'nowrap',
                  px: { xs: 1, sm: 2 },
                  '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 } },
                }}
              >
                <ActionLabel shortLabel="Exports" fullLabel="View exports" />
              </Button>
            )}
            {canExportOrders && (
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={() => setExportDialogOpen(true)}
                aria-label="Export purchase orders"
                sx={{
                  height: 40,
                  flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
                  minWidth: { xs: 0, sm: 'auto' },
                  whiteSpace: 'nowrap',
                  px: { xs: 1, sm: 2 },
                  '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 } },
                }}
              >
                <ActionLabel shortLabel="Export" fullLabel="Export Purchase Orders" />
              </Button>
            )}
            {canCreateOrder && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/orders/purchase/create')}
                aria-label="Create order"
                sx={{
                  bgcolor: '#e90000',
                  '&:hover': { bgcolor: '#c00000' },
                  height: 40,
                  flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
                  minWidth: { xs: 0, sm: 'auto' },
                  whiteSpace: 'nowrap',
                  px: { xs: 1, sm: 2 },
                  '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 } },
                }}
              >
                <ActionLabel shortLabel="Create" fullLabel="Create order" />
              </Button>
            )}
          </Box>
        )}
      </Box>

      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2, width: '100%', minWidth: 0, wordBreak: 'break-word' }}
        >
          {error}
        </Alert>
      )}

      <OrdersListTable
        orders={orders}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        isSales={isSales}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onRowClick={handleRowClick}
      />

      {!isSales && canExportOrders && (
        <PurchaseOrderExportDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          onQueued={handleExportQueued}
          listFilters={{
            q: debouncedSearch,
            status: statusFilter,
            paymentStatus: paymentStatusFilter,
            orgIds: selectedOrgIds,
          }}
          listMatchCount={totalCount}
        />
      )}
    </Box>
  )
}

export default OrdersListPage
